import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';

function formatTime(ts) {
  const d = new Date(ts);
  const now = Date.now();
  const diff = now - d;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString();
}

function stepToTab(step, chapterNumber) {
  if (step === 'summarize') return 'gameContext';
  if (step === 'outline') return 'gameScenario';
  if (step === 'chapter') return `chapter${chapterNumber ?? 1}`;
  if (step === 'conclusion') return 'conclusion';
  if (step === 'introduction') return 'gameIntro';
  return null;
}

export default function NotificationCenter() {
  const [anchorEl, setAnchorEl] = useState(null);
  const { isAuthenticated } = useAuth();
  const { notifications, unreadCount, markRead, clearAll, stepLabel } = useNotifications();
  const ui = useUI();
  const navigate = useNavigate();

  if (!isAuthenticated) return null;

  const open = Boolean(anchorEl);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleItemClick = (notification) => {
    markRead(notification.id);
    handleClose();
    const tab = stepToTab(notification.step, notification.chapterNumber);
    if (tab && ui?.setActiveTab) ui.setActiveTab(tab);
    const path = `/game-creation-continue/${notification.gameId}`;
    navigate(tab ? `${path}?tab=${tab}` : path);
  };

  const handleClearAll = () => {
    clearAll();
    handleClose();
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleOpen}
        aria-label="Creation notifications"
        sx={{ color: '#424242' }}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 320, maxWidth: 400, maxHeight: 400 } }}
      >
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Game creation
          </Typography>
        </Box>
        <List dense sx={{ overflow: 'auto' }}>
          {notifications.length === 0 ? (
            <ListItemButton disabled>
              <ListItemText primary="No notifications" secondary="You'll see updates here when steps finish." />
            </ListItemButton>
          ) : (
            [...notifications].reverse().map((n) => (
              <ListItemButton
                key={n.id}
                onClick={() => handleItemClick(n)}
                sx={{
                  borderLeft: 3,
                  borderColor: n.status === 'failed' ? 'error.main' : 'transparent',
                }}
              >
                {n.status === 'failed' ? (
                  <ErrorOutlineIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
                ) : null}
                <ListItemText
                  primary={
                    <Typography variant="body2" noWrap>
                      {n.gameTitle || `Game`} – {stepLabel(n.step, n.chapterNumber)} {n.status === 'failed' ? 'failed' : 'ready'}
                    </Typography>
                  }
                  secondary={n.status === 'failed' ? n.error : formatTime(n.timestamp)}
                />
              </ListItemButton>
            ))
          )}
        </List>
        {notifications.length > 0 && (
          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
            <Button size="small" onClick={handleClearAll} fullWidth>
              Clear all
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
}
